/* Bundle offer add-to-cart widget for the `bundel-product` template.
   - "Enkel product"  -> adds the selected variant x quantity
   - "Bundelaanbieding" -> adds 2 chosen variants of this product (the "2 voor" bundle)
   Uses the theme AJAX cart (cart-store) and opens the cart drawer, exactly like <product-form>. */
import Cart from "cart-store";
import { formatMoney } from "utils";

class BundleOffer extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector("script.bundle-offer__data");
    if (!dataEl) return;

    this.data = JSON.parse(dataEl.textContent);
    this.variants = this.data.variants;
    this.options = this.data.options;
    this.moneyFormat = this.data.moneyFormat || "{{ amount }}";

    this.mode = "single"; // "single" | "bundle"
    this.stagedAction = this.querySelector("staged-action");
    this.addButton = this.querySelector(".bundle-offer__submit");
    this.errorEl = this.querySelector(".bundle-offer__error");
    this.singlePriceEl = this.querySelector('.bundle-offer__option[data-mode="single"] .bundle-offer__price');

    // Single mode uses the theme's own variant picker. We track its selection
    // through the product form's hidden id input (kept in sync by the theme).
    this.sectionId = this.dataset.sectionId;
    this.#bindThemeVariant();

    this.#bindOptionToggle();
    this.#bindRows();
    this.#bindQuantity();
    this.#bindSubmit();

    // Initial render of bundle rows + single price + global state.
    this.querySelectorAll(".bundle-offer__row").forEach((row) => this.#updateRow(row));
    this.#updateSinglePrice();
    this.#refreshState();
  }

  /* --- Single mode: follow the theme variant picker --------------------- */
  #bindThemeVariant() {
    const form =
      (this.sectionId && document.getElementById(`product-form-${this.sectionId}`)) ||
      document.querySelector('product-single form[id^="product-form-"]');
    this.variantInput = form ? form.querySelector('input[name="id"]') : null;

    if (this.variantInput) {
      // The theme dispatches a bubbling "change" on this input on every swap.
      this.variantInput.addEventListener("change", () => {
        this.#updateSinglePrice();
        this.#refreshState();
      });
    }
  }

  #singleVariant() {
    const id = this.variantInput ? Number(this.variantInput.value) : null;
    return this.variants.find((v) => v.id === id) || this.variants.find((v) => v.available) || this.variants[0];
  }

  #updateSinglePrice() {
    const v = this.#singleVariant();
    if (v && this.singlePriceEl) this.singlePriceEl.textContent = this.#formatMoney(v.price);
  }

  /* --- Variant resolution ----------------------------------------------- */
  #resolveVariant(row) {
    const selects = Array.from(row.querySelectorAll("select[data-option-position]")).sort(
      (a, b) => a.dataset.optionPosition - b.dataset.optionPosition
    );
    // Products with a single (default) variant render no selects.
    if (selects.length === 0) {
      return this.variants.find((v) => v.available) || this.variants[0];
    }
    const selected = selects.map((sel) => sel.value);
    return this.variants.find(
      (v) => v.options.length === selected.length && v.options.every((o, i) => o === selected[i])
    );
  }

  #formatMoney(cents) {
    return formatMoney(cents, this.moneyFormat);
  }

  /* --- Option group (single vs bundle) ---------------------------------- */
  #bindOptionToggle() {
    this.querySelectorAll('input[name="bundle-offer-mode"]').forEach((radio) => {
      radio.addEventListener("change", () => {
        this.mode = radio.value;
        this.querySelectorAll(".bundle-offer__option").forEach((opt) => {
          opt.classList.toggle("is-selected", opt.dataset.mode === this.mode);
        });
        this.#refreshState();
      });
    });
    // Allow clicking anywhere on the option card to select it.
    this.querySelectorAll(".bundle-offer__option").forEach((opt) => {
      opt.addEventListener("click", (e) => {
        if (e.target.closest("select, button, input")) return;
        const radio = opt.querySelector('input[name="bundle-offer-mode"]');
        if (radio && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    });
  }

  /* --- Variant rows ----------------------------------------------------- */
  #bindRows() {
    this.querySelectorAll(".bundle-offer__row select[data-option-position]").forEach((sel) => {
      sel.addEventListener("change", () => {
        this.#updateRow(sel.closest(".bundle-offer__row"));
        this.#refreshState();
      });
    });
  }

  #updateRow(row) {
    const variant = this.#resolveVariant(row);
    row._variant = variant;

    // Thumbnail
    const img = row.querySelector(".bundle-offer__thumb img");
    if (img && variant && variant.image) img.src = variant.image;

    this.#updateAvailabilityLabels(row);
  }

  // Mark each option (per select) as sold out based on the OTHER selected
  // options in the same row, e.g. size "S" sold out for the chosen colour.
  #updateAvailabilityLabels(row) {
    const selects = Array.from(row.querySelectorAll("select[data-option-position]"));
    selects.forEach((sel) => {
      const position = Number(sel.dataset.optionPosition);
      Array.from(sel.options).forEach((opt) => {
        const available = this.#isComboAvailable(position, opt.value, selects);
        opt.textContent = available ? opt.value : `${opt.value} — ${this.data.soldOutSuffix}`;
        opt.classList.toggle("is-unavailable", !available);
      });
    });
  }

  // Is there an available variant matching `candidate` at `position`, while
  // honouring the current values of every OTHER select in the row?
  #isComboAvailable(position, candidate, selects) {
    return this.variants.some((v) => {
      if (!v.available) return false;
      if (v.options[position - 1] !== candidate) return false;
      return selects.every((sel) => {
        const p = Number(sel.dataset.optionPosition);
        return p === position || v.options[p - 1] === sel.value;
      });
    });
  }

  /* --- Quantity (single mode only) -------------------------------------- */
  #bindQuantity() {
    const wrap = this.querySelector(".bundle-offer__qty");
    if (!wrap) return;
    const input = wrap.querySelector("input");
    wrap.querySelectorAll("button[data-step]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const next = Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(btn.dataset.step, 10));
        input.value = next;
      });
    });
    input.addEventListener("change", () => {
      input.value = Math.max(1, parseInt(input.value, 10) || 1);
    });
  }

  #quantity() {
    const input = this.querySelector(".bundle-offer__qty input");
    return input ? Math.max(1, parseInt(input.value, 10) || 1) : 1;
  }

  /* --- Items to add, availability, button state ------------------------- */
  #bundleRows() {
    return Array.from(this.querySelectorAll('.bundle-offer__row[data-row^="bundle"]'));
  }

  #items() {
    if (this.mode === "single") {
      const v = this.#singleVariant();
      return v ? [{ id: v.id, quantity: this.#quantity() }] : [];
    }
    return this.#bundleRows()
      .map((row) => row._variant)
      .filter(Boolean)
      .map((v) => ({ id: v.id, quantity: 1 }));
  }

  #allAvailable() {
    if (this.mode === "single") {
      const v = this.#singleVariant();
      return !!(v && v.available);
    }
    const rows = this.#bundleRows();
    return rows.length > 0 && rows.every((row) => row._variant && row._variant.available);
  }

  #refreshState() {
    const available = this.#allAvailable();
    const label = this.addButton.querySelector(".bundle-offer__submit-label");
    if (label) {
      label.textContent = available ? this.data.addLabel : this.data.soldOutLabel;
    }
    this.addButton.disabled = !available;
    this.addButton.classList.toggle("is-disabled", !available);
  }

  /* --- Add to cart ------------------------------------------------------ */
  #bindSubmit() {
    this.addButton.addEventListener("click", (e) => {
      e.preventDefault();
      this.#submit();
    });
  }

  async #submit() {
    const items = this.#items();
    if (!items.length || !this.#allAvailable()) return;
    this.#clearError();
    try {
      if (this.stagedAction) await this.stagedAction.setState("DOING");
      await Cart.add(items);
      if (this.stagedAction) await this.stagedAction.setState("DONE");
      await Cart.resetVariantsBeingAdded();
      // Open the cart drawer / notification, mirroring product-form.js.
      const cart = document.querySelector(".modal--cart, .popup--cart-notification");
      if (cart && !cart.shown) {
        cart.open();
      } else if (cart && cart.interval) {
        cart.interval.reset();
        cart.interval.start();
      }
    } catch (error) {
      this.#showError(error?.message || "");
      if (this.stagedAction) await this.stagedAction.setState("IDLE");
    }
  }

  #showError(message) {
    if (this.errorEl) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }
  }

  #clearError() {
    if (this.errorEl) {
      this.errorEl.textContent = "";
      this.errorEl.hidden = true;
    }
  }
}

if (!customElements.get("bundle-offer")) {
  customElements.define("bundle-offer", BundleOffer);
}
